import * as helios from "@hyperionbt/helios";
import { DatumOffer } from "kreate-common/modules/business-types";
import { CLIENT_ENV } from "kreate-env/client";

import ScriptOffer from "../../onchain/scriptOffer.hl";
import { getCardanoNetworkParams, toHeliosDatum } from "../../utils";

type Params = {
  treasuryAddress: helios.Address;
  wallet: helios.Cip30Wallet;
  datumOffer: DatumOffer;
  txMessage: string | null | undefined;
};

const MIN_DEPOSIT = 2_000_000;

export async function buildPartialTx$CreateOffer({
  treasuryAddress,
  wallet,
  datumOffer,
  txMessage,
}: Params) {
  const walletHelper = new helios.WalletHelper(wallet);
  const address = await walletHelper.baseAddress;

  const artworkFee = datumOffer.wantedQuantity * datumOffer.editionPrice;
  const deposit = BigInt(Math.max(artworkFee, MIN_DEPOSIT));
  const [utxos, remainingUtxos] = await walletHelper.pickUtxos(
    new helios.Value(deposit),
    helios.CoinSelection.selectLargestFirst
  );

  const tx = new helios.Tx();

  tx.addInputs(utxos);

  const scriptOfferAddress = helios.Address.fromValidatorHash(
    new ScriptOffer({ TREASURY_ADDRESS: treasuryAddress }).compile(true)
      .validatorHash,
    new helios.StakeKeyHash(
      helios.hexToBytes(CLIENT_ENV.SCRIPT_STAKE_KEY_HASH)
    ),
    CLIENT_ENV.CARDANO_NETWORK !== "Mainnet"
  );

  tx.addOutput(
    new helios.TxOutput(
      scriptOfferAddress,
      new helios.Value(deposit),
      toHeliosDatum({ type: "offer", value: datumOffer })
    )
  );

  // Protocol fee
  let protocolFee = 0;
  // For reservation (primary) only:
  // The extra fee covers for min ADA when fulfilling Editions
  // to the Offerer plus transaction fees.
  if (!datumOffer.isSecondary) {
    protocolFee = CLIENT_ENV.MIN_PROTOCOL_FEE;
    if (artworkFee >= MIN_DEPOSIT) {
      protocolFee += CLIENT_ENV.MIN_PROTOCOL_FEE;
    }
    tx.addOutput(
      new helios.TxOutput(
        // Paid to moderator address to use for fulfillment
        new helios.Address(CLIENT_ENV.ADDRESS_MODERATOR),
        new helios.Value(BigInt(protocolFee))
      )
    );
  }

  /** NOTE: @thanh-kreate
   * Since helios api does not automatically break a string that is less than 64
   * characters to array, this should be done manually to satisfy CIP-20 constraints
   * More details: https://cips.cardano.org/cips/cip20/
   * */
  txMessage != null &&
    tx.addMetadata(674, {
      map: [
        [
          "msg",
          helios.textToBytes(txMessage).length <= 64 ? [txMessage] : txMessage,
        ],
      ],
    });

  await tx.finalize(
    getCardanoNetworkParams(CLIENT_ENV.CARDANO_NETWORK),
    address,
    remainingUtxos
  );

  return {
    tx,
    artworkFee,
    protocolFee:
      artworkFee >= MIN_DEPOSIT
        ? protocolFee
        : protocolFee + MIN_DEPOSIT - artworkFee,
    txFee: Number(tx.body.fee),
  };
}
